import React, { useState, useEffect, useRef } from "react";
import { ListBuilder, FormBuilder, Form } from "@redriver/cinnamon";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { Button, Icon } from "semantic-ui-react";
import { AppRoutes } from "constants/routes";
import { EntityType } from "constants/enums";
import { FileIcon, DirectoryIcon, SitesIcon } from "components/icons";
import { searchWorkspace } from "./actions";

const SearchDebounceMs = 300;
const MaxResultsPerGroup = 5;

const redirectRoutes = {
  [EntityType.Site]: AppRoutes.SiteRedirect,
  [EntityType.Directory]: AppRoutes.DirectoryRedirect,
  [EntityType.File]: AppRoutes.FileRedirect,
};

const highlightTerm = (text, term) => {
  const lowerText = text.toLowerCase();
  const lowerTerm = term.toLowerCase();
  if (!lowerTerm) return text;

  const parts = [];
  let position = 0;
  let matchIndex;
  while ((matchIndex = lowerText.indexOf(lowerTerm, position)) !== -1) {
    if (matchIndex > position) parts.push(text.substring(position, matchIndex));
    parts.push(
      <mark key={matchIndex}>
        {text.substring(matchIndex, matchIndex + lowerTerm.length)}
      </mark>
    );
    position = matchIndex + lowerTerm.length;
  }
  parts.push(text.substring(position));
  return parts;
};

const ResultIcon = ({ item }) => {
  switch (item.type) {
    case EntityType.Site:
      return <SitesIcon className="site-icon" />;
    case EntityType.Directory:
      return <DirectoryIcon />;
    default:
      return <FileIcon file={item} />;
  }
};

const toGroups = (data) => {
  if (!data) return [];
  return [
    { key: "sites", title: "Sites", ...data.sites },
    { key: "folders", title: "Folders", ...data.folders },
    { key: "documents", title: "Documents", ...data.documents },
  ].filter((g) => g.results && g.results.length > 0);
};

const countResults = (data) =>
  data
    ? data.sites.totalResults +
      data.folders.totalResults +
      data.documents.totalResults
    : 0;

let popupSeq = 0;

const WorkspaceSearch = ({ className, siteId, siteName }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const popupId = useRef(`workspace-search-popup-${++popupSeq}`).current;

  const scoped = !!siteId;

  const placeholder = scoped
    ? `Search within ${siteName || "this site"}…`
    : "Search sites, folders and documents…";
  const hint = scoped
    ? `Start typing to search folders and documents in ${
        siteName || "this site"
      }.`
    : "Start typing to search across all your workspaces, folders, and documents by name.";

  useEffect(() => {
    const onClickAway = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  return (
    <div
      className={classNames("workspace-search", className)}
      ref={containerRef}
    >
      <Button
        type="button"
        circular
        className="workspace-search-toggle"
        active={open}
        onClick={() => setOpen((o) => !o)}
        aria-label={placeholder}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
      >
        <Icon name="search" fitted aria-hidden />
      </Button>
      {open && (
        <div
          className="workspace-search-popup"
          id={popupId}
          role="dialog"
          aria-label={placeholder}
        >
          <ListBuilder
            loadAction={(listState) => {
              const term = (listState.filters.term || "").trim();
              if (!term) {
                return () => Promise.resolve({ success: true, response: null });
              }
              return searchWorkspace({
                term,
                maxResultsPerGroup: MaxResultsPerGroup,
                siteId,
              });
            }}
            initialFilters={{ term: "" }}
            dataTransform={toGroups}
            totalTransform={countResults}
            debounceFilters={SearchDebounceMs}
            renderList={(groups, state, events) => {
              const term = (state.filters.term || "").trim();
              return (
                <React.Fragment>
                  <div className="workspace-search-input">
                    <FormBuilder
                      value={state.filters}
                      onChange={events.onChangeFilters}
                      renderForm={(formProps) => (
                        <Form {...formProps}>
                          <Form.Input
                            field="term"
                            icon="search"
                            iconPosition="left"
                            placeholder={placeholder}
                            loading={state.loading}
                            autoFocus
                            fluid
                          />
                        </Form>
                      )}
                    />
                    <button
                      type="button"
                      className="esc-hint"
                      aria-label="Close search"
                      onClick={() => setOpen(false)}
                    >
                      esc
                    </button>
                  </div>
                  <div className="workspace-search-body">
                    {!term ? (
                      <div className="search-hint">{hint}</div>
                    ) : groups.length === 0 && !state.loading ? (
                      <div className="no-results">
                        <p>No results found for &ldquo;{term}&rdquo;</p>
                        <p>Try different search terms</p>
                      </div>
                    ) : (
                      groups.map(({ key, title, results, totalResults }) => (
                        <div className="result-group" key={key}>
                          <div className="group-header">
                            <span>{title}</span>
                            {totalResults > results.length && (
                              <span className="group-count">
                                Showing {results.length} of {totalResults}
                              </span>
                            )}
                          </div>
                          {results.map((item) => (
                            <Link
                              className="result-item"
                              key={item.id}
                              to={redirectRoutes[item.type](item.id)}
                              onClick={() => setOpen(false)}
                            >
                              <ResultIcon item={item} />
                              <div className="result-details">
                                <div className="result-name">
                                  {highlightTerm(item.name, term)}
                                </div>
                                {item.path && item.path.length > 0 && (
                                  <div className="result-path">
                                    {item.path.join(" / ")}
                                  </div>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </React.Fragment>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default WorkspaceSearch;
